/**
 * Top-level engine reducer for @cloudnest/cascadia-engine.
 *
 * `applyAction` is the single ergonomic entry point that combines
 * validate + apply into one call.
 *
 * Invariants (enforced, never thrown):
 * - If validation fails, the function returns ApplyActionFailure immediately.
 *   No clone, no work. The input `state` is untouched.
 * - If validation succeeds, the applier is called and its result is returned
 *   as ApplyActionSuccess.
 * - `Init` is handled specially: the `state` arg is ignored, and `initGame`
 *   is called directly with parameters extracted from the action.
 * - No `Math.random()` — RNG is seeded deterministically inside `initGame`.
 * - No mutations of input state.
 * - No I/O.
 *
 * @module engine
 */

import { dispatchApplier } from "./appliers";
import { initGame } from "./init-game";
import type { Action, InitAction } from "./types/actions";
import type { ValidationResult } from "./types/common";
import type { GameState, ScoringCardSelection } from "./types/game-state";
import type { Wildlife } from "./types/wildlife";
import { dispatchValidator } from "./validators";

// ---------------------------------------------------------------------------
// Public result types
// ---------------------------------------------------------------------------

export interface ApplyActionSuccess {
  readonly ok: true;
  readonly state: GameState;
}

export interface ApplyActionFailure {
  /** When ok === false, the reducer did no work. The input state is untouched. */
  readonly ok: false;
  readonly validation: ValidationResult;
}

export type ApplyActionResult = ApplyActionSuccess | ApplyActionFailure;

// ---------------------------------------------------------------------------
// Init action adapter
//
// `InitAction.scoringCards` is a readonly array of { wildlife, cardId } pairs.
// `InitGameInput.scoringCards` is a `ScoringCardSelection` object (keyed by species).
// This adapter converts between the two shapes.
// ---------------------------------------------------------------------------

const adaptInitActionToInitGameInput = (action: InitAction) => {
  const scoringCards: Partial<Record<Wildlife, string>> = {};
  for (const entry of action.scoringCards) {
    scoringCards[entry.wildlife] = entry.cardId;
  }

  return {
    gameId: `game-${action.seed}`,
    seed: action.seed,
    playerSeating: action.playerSeating,
    scoringCards: scoringCards as ScoringCardSelection,
    modulesOn: action.modulesOn,
    hostPlayerId: action.hostPlayerId,
    createdAt: new Date().toISOString(),
  };
};

// ---------------------------------------------------------------------------
// applyAction
// ---------------------------------------------------------------------------

/**
 * Validates `action` against `state`, then applies it if valid.
 *
 * Special case — `Init`:
 *   The dispatcher validator returns ERR_UNKNOWN_ACTION for Init by design.
 *   `applyAction` intercepts Init before calling the dispatcher and routes
 *   directly to `initGame`. The `state` argument is ignored for Init.
 *
 * @returns ApplyActionSuccess when the action is valid and applied.
 * @returns ApplyActionFailure when validation fails. No state mutation occurs.
 */
export const applyAction = (
  state: GameState,
  action: Action,
): ApplyActionResult => {
  // Init is special: bypass the dispatcher, call initGame directly.
  if (action.type === "Init") {
    const input = adaptInitActionToInitGameInput(action);
    const result = initGame(input);
    if (!result.ok) {
      return {
        ok: false,
        validation: { valid: false, error: result.error, details: {} },
      };
    }
    return { ok: true, state: result.value };
  }

  // All other actions: validate first.
  const validation = dispatchValidator(state, action);
  if (!validation.valid) {
    return { ok: false, validation };
  }

  // Validation passed — apply.
  const next = dispatchApplier(state, action);
  return { ok: true, state: next };
};
